import type { ConditionGroup, SectionDef, TemplateDefinition } from '../forms/types.js';
import { ACCESS_STATUS_OPTIONS, NO_PASSWORDS_NOTICE, SectionBuilder } from './builder.js';
import { buildC01Section, C01_CONFIRMATION } from './c01.js';

/** F03 — Comprehensive SEO + GMB + AEO + GEO + AIO + SXO Onboarding (11 numbered sections + C01). */

const isTrue = (field: string): ConditionGroup => ({ all: [{ field, op: 'equals', value: true }] });

function sec(n: number, key: string, title: string) {
  const nn = String(n).padStart(2, '0');
  return new SectionBuilder(`f03_s${nn}`, { number: `F03.${nn}`, key, title });
}

function accessItem(sb: SectionBuilder, key: string, label: string, provider: string, roleHint: string) {
  sb.add(`${key}_status`, 'single_select', label, {
    options: ACCESS_STATUS_OPTIONS,
    accessProvider: provider,
    help: `Access status (Pending/Granted). Appropriate role: ${roleHint}.`,
  });
  sb.add(`${key}_invitee`, 'email', `${label}: Access Email/Invitee`, {
    accessProvider: provider,
    requiredWhen: { all: [{ field: sb.id(`${key}_status`), op: 'equals', value: sb.optionId(`${key}_status`, 'granted') }] },
  });
  sb.add(`${key}_role`, 'short_text', `${label}: Appropriate Role`, { accessProvider: provider, placeholder: roleHint });
}

function assetItem(sb: SectionBuilder, key: string, label: string, category: string, sensitivity?: 'confidential' | 'restricted') {
  const extra = { assetCategory: category, ...(sensitivity ? { sensitivity } : {}) };
  sb.add(`${key}_provided`, 'yes_no', label, { ...extra, help: 'Provided? (Yes/No)' });
  const vis = isTrue(sb.id(`${key}_provided`));
  sb.add(`${key}_file`, 'file_upload', `${label}: File`, { ...extra, visibleWhen: vis });
  sb.add(`${key}_link`, 'asset_link', `${label}: Link`, { ...extra, visibleWhen: vis });
}

function s01(): SectionDef {
  const sb = sec(1, 'business', 'Business Information');
  sb.add('company_brand', 'short_text', 'Company/Brand', { required: true, profileKey: 'public_brand' });
  sb.add('legal_name', 'short_text', 'Legal Name', { profileKey: 'legal_name' });
  sb.add('contact', 'short_text', 'Contact', { required: true, profileKey: 'primary_contact_name' });
  sb.add('designation', 'short_text', 'Designation', { profileKey: 'primary_contact_designation' });
  sb.add('website', 'url', 'Website', { required: true, profileKey: 'website' });
  sb.add('industry', 'short_text', 'Industry/Niche', { required: true, profileKey: 'industry' });
  sb.add('official_email', 'email', 'Official Email', { required: true, profileKey: 'email' });
  sb.add('phone', 'phone', 'Phone', { required: true, profileKey: 'phone' });
  sb.add('business_overview', 'long_text', 'Business Overview & Value Proposition', { required: true, profileKey: 'business_description' });
  sb.add('products_priorities', 'long_text', 'Products/Services & Commercial Priorities', { required: true });
  return sb.build();
}

function s02(): SectionDef {
  const sb = sec(2, 'goals_audience', 'Goals, Audience & Markets');
  sb.add('growth_goals', 'multi_select', 'Goals', {
    required: true,
    options: [
      'Organic growth',
      'Leads',
      'Revenue',
      'Brand authority',
      'Local visibility',
      { label: 'AI/search visibility', key: 'ai_search_visibility' },
      'Conversions',
    ],
  });
  sb.add('business_goals', 'long_text', 'Business goals', { required: true });
  sb.add('audience_personas', 'long_text', 'Audience/personas');
  sb.add('locations_languages', 'long_text', 'Locations/countries/languages');
  return sb.build();
}

function s03(): SectionDef {
  const sb = sec(3, 'seo', 'SEO Scope');
  sb.add('seo_scope', 'multi_select', 'SEO Scope', {
    options: [
      'Technical audit',
      { label: 'Crawl/indexation', key: 'crawl_indexation' },
      { label: 'CWV', key: 'cwv' },
      'Schema',
      'Internal linking',
      { label: 'On-page', key: 'on_page' },
      'Content strategy',
      'Backlink strategy',
      { label: 'Digital PR', key: 'digital_pr' },
      { label: 'Local SEO', key: 'local_seo' },
    ],
  });
  sb.add('seo_issues', 'long_text', 'Issues');
  sb.add('priority_pages_keywords', 'long_text', 'Priority pages/keywords');
  sb.add('existing_reports', 'long_text', 'Existing reports/audits/backlink data');
  sb.add('existing_reports_link', 'asset_link', 'Reports/audits link', { assetCategory: 'reports' });
  return sb.build();
}

function s04(): SectionDef {
  const sb = sec(4, 'gmb', 'Google Business Profile (GMB)');
  sb.add('gbp_business_name', 'short_text', 'Business Name');
  sb.add('gbp_primary_category', 'short_text', 'Primary Category');
  sb.add('gbp_address', 'long_text', 'Address');
  sb.add('gbp_service_areas', 'long_text', 'Service Areas');
  sb.add('gbp_phone', 'phone', 'Phone');
  sb.add('gbp_website', 'url', 'Website');
  sb.add('gbp_hours', 'weekly_hours', 'Hours');
  sb.add('gbp_url', 'url', 'GBP URL');
  sb.add('gbp_owner_manager_access', 'single_select', 'Owner/Manager Access', {
    accessProvider: 'gbp',
    options: ['Owner access available', 'Manager access available', 'No access', 'Not sure'],
  });
  sb.add('local_priorities', 'long_text', 'Local priorities/locations');
  sb.add('reviews_reputation', 'long_text', 'Reviews/reputation/citations');
  return sb.build();
}

function s05(): SectionDef {
  const sb = sec(5, 'aeo', 'Answer Engine Optimization (AEO)');
  sb.add('aeo_focus', 'multi_select', 'AEO Focus Areas', {
    options: [
      { label: 'FAQs', key: 'faqs' },
      'Question-based',
      'How-to',
      'Expert answers',
      { label: 'Service/product answers', key: 'service_product_answers' },
      { label: 'PAA', key: 'paa' },
    ],
  });
  sb.add('customer_questions', 'long_text', 'Customer questions');
  sb.add('authority_information', 'long_text', 'Authority information');
  sb.add('faq_knowledge_content', 'long_text', 'FAQ/knowledge content');
  return sb.build();
}

function s06(): SectionDef {
  const sb = sec(6, 'geo', 'Generative Engine Optimization (GEO)');
  sb.info(
    'client_provided_note',
    'Entity and authority information in this section is client-provided and is not independently verified.',
    'Client-provided information',
  );
  sb.add('geo_focus', 'multi_select', 'GEO Focus Areas', {
    options: [
      'Entity information',
      'Brand authority',
      'Citations',
      'Third-party references',
      { label: 'Digital PR', key: 'digital_pr' },
      'Expert mentions',
      'Structured business info',
    ],
  });
  sb.add('entity_facts', 'long_text', 'Entity facts');
  sb.add('authoritative_references', 'long_text', 'Authoritative references');
  sb.add('mentions_publications', 'long_text', 'Mentions/publications');
  return sb.build();
}

function s07(): SectionDef {
  const sb = sec(7, 'aio', 'AI Optimization (AIO)');
  sb.add('aio_focus', 'multi_select', 'AIO Focus Areas', {
    options: [
      { label: 'AI-search visibility', key: 'ai_search_visibility' },
      'Entity consistency',
      'Structured content',
      'Knowledge sources',
      'Brand mentions',
      { label: 'Author/expert profiles', key: 'author_expert_profiles' },
    ],
  });
  sb.add('important_entities', 'long_text', 'Important entities/people/products/services/relationships');
  sb.add('trusted_sources', 'long_text', 'Trusted content/data sources');
  const r = 'entities';
  sb.add(r, 'repeater', 'Entities', {
    rowLabel: 'Entity',
    validation: { maxRows: 100 },
    children: [
      sb.child(r, 'entity', 'short_text', 'Entity', { required: true }),
      sb.child(r, 'type', 'single_select', 'Type', {
        options: ['Organization', 'Person', 'Product', 'Service', 'Place', { label: 'Other', key: 'other', opensDetail: true }],
      }),
      sb.child(r, 'relationship', 'long_text', 'Relationship/Notes'),
      sb.child(r, 'reference_url', 'url', 'Reference URL'),
    ],
  });
  return sb.build();
}

function s08(): SectionDef {
  const sb = sec(8, 'sxo', 'Search Experience Optimization (SXO)');
  sb.add('sxo_focus', 'multi_select', 'SXO Focus Areas', {
    options: [
      'Search intent mapping',
      { label: 'UX review', key: 'ux_review' },
      'Page experience',
      'Content quality',
      'Navigation',
      { label: 'CTAs', key: 'ctas' },
      'Conversion journey',
      'Trust signals',
      'Mobile experience',
    ],
  });
  sb.add('conversion_journey_notes', 'long_text', 'Conversion journey');
  sb.add('cta_preferences', 'long_text', 'CTA preferences/goals');
  sb.add('ux_constraints', 'long_text', 'UX constraints/issues');
  return sb.build();
}

function s09(): SectionDef {
  const sb = sec(9, 'brand_assets', 'Brand, Content & Assets');
  assetItem(sb, 'brand_guidelines', 'Brand guidelines', 'brand_guidelines');
  assetItem(sb, 'logo', 'Logo', 'logo');
  assetItem(sb, 'colors', 'Colors', 'colors');
  assetItem(sb, 'fonts', 'Fonts', 'fonts');
  assetItem(sb, 'photography', 'Photography', 'imagery');
  assetItem(sb, 'video', 'Video', 'video');
  assetItem(sb, 'case_studies', 'Case studies', 'case_studies');
  assetItem(sb, 'testimonials', 'Testimonials', 'testimonials');
  assetItem(sb, 'product_data', 'Product data', 'product_data');
  assetItem(sb, 'legal', 'Legal/compliance', 'legal_documents', 'confidential');
  sb.add('tone_voice', 'long_text', 'Tone/voice');
  sb.add('topics_promote_avoid', 'long_text', 'Topics to promote/avoid');
  sb.add('asset_folder', 'asset_link', 'Asset folder', { assetCategory: 'brand_kit' });
  return sb.build();
}

function s10(): SectionDef {
  const sb = sec(10, 'competitors', 'Competitors & References');
  sb.info(
    'competitor_note',
    'Add one row per competitor. Each competitor can carry several references (website, social profile, GBP profile and another reference).',
    'Competitors',
  );
  const r = 'competitors';
  sb.add(r, 'repeater', 'Competitors', {
    rowLabel: 'Competitor',
    validation: { maxRows: 50 },
    children: [
      sb.child(r, 'competitor', 'short_text', 'Competitor/brand', { required: true }),
      sb.child(r, 'website', 'url', 'Website'),
      sb.child(r, 'social_profile', 'url', 'Social profile'),
      sb.child(r, 'gbp_profile', 'url', 'GBP profile'),
      sb.child(r, 'other_reference', 'url', 'Other reference'),
      sb.child(r, 'notes', 'long_text', 'Notes'),
    ],
  });
  return sb.build();
}

function s11(): SectionDef {
  const sb = sec(11, 'access_reporting', 'Access, Approvals & Reporting');
  sb.info('no_passwords', NO_PASSWORDS_NOTICE, 'Access instructions');
  accessItem(sb, 'gsc', 'GSC', 'gsc', 'Full or Restricted user');
  accessItem(sb, 'ga4', 'GA4', 'ga4', 'Viewer/Analyst');
  accessItem(sb, 'gtm', 'GTM', 'gtm', 'Read/Edit as required');
  accessItem(sb, 'cms', 'CMS', 'cms', 'Admin or Editor as required');
  accessItem(sb, 'gbp', 'GBP Owner/Manager', 'gbp', 'Owner or Manager');
  accessItem(sb, 'other', 'Social/Other appropriate role', 'other', 'Appropriate role for the platform');
  sb.info(
    'granted_declaration',
    '“Granted” is a declaration by the client. Access is verified by the team before it is relied upon.',
    'Access verification',
  );
  sb.add('primary_approval_contact', 'short_text', 'Primary Approval Contact', { required: true });
  sb.add('technical_contact', 'short_text', 'Technical/Developer Contact');
  sb.add('content_approval_contact', 'short_text', 'Content Approval Contact');
  sb.add('reporting_frequency', 'single_select', 'Reporting frequency', {
    required: true,
    options: ['Weekly', { label: 'Biweekly', key: 'biweekly' }, 'Monthly', { label: 'Custom', key: 'custom', opensDetail: true }],
  });
  sb.add('reporting_kpis', 'long_text', 'KPIs');
  sb.add('dashboards', 'long_text', 'Dashboards');
  sb.add('strategy_notes', 'long_text', 'Strategy notes');
  return sb.build();
}

export const F03_COMPREHENSIVE: TemplateDefinition = {
  key: 'comprehensive_search_onboarding',
  code: 'F03',
  name: 'Comprehensive SEO + GMB + AEO + GEO + AIO + SXO Onboarding',
  description: 'Onboarding questionnaire for comprehensive search engagements (SEO, GMB, AEO, GEO, AIO and SXO).',
  serviceModule: 'comprehensive_search',
  settings: { biweeklyMeaning: 'every_two_weeks' },
  sections: [s01(), s02(), s03(), s04(), s05(), s06(), s07(), s08(), s09(), s10(), s11(), buildC01Section('f03')],
  confirmation: C01_CONFIRMATION,
};
